/*
 * Throwaway preview — proposed /s/:handle public seller profile page.
 * No backend calls; pure visual mockup. Hit /preview/seller-profile to view.
 *
 * Toggle the "Viewer" pill in the header to see how the SAME URL renders
 * differently for an anonymous visitor vs a logged-in buyer vs the seller
 * themselves (the auth-aware hybrid recommended in the chat).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, MapPin, Calendar, CheckCircle2, Star, MessageSquare,
  Package, Truck, RotateCcw, AlertTriangle, CreditCard, Clock, Sparkles,
  Building2, Globe, Mail, Edit3, ArrowUpRight, ChevronRight, Info, Lock,
} from "lucide-react";

const VIEWERS = [
  { key: "anon",   label: "Anonymous"  },
  { key: "buyer",  label: "Logged-in buyer" },
  { key: "seller", label: "The seller themselves" },
];

export default function SellerProfilePreview() {
  const [viewer, setViewer] = useState("anon");
  return (
    <div className="min-h-screen bg-secondary/30">
      {/* ── Sticky preview header — viewer toggle + URL pill ─────────── */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1240px] mx-auto px-8 py-4 flex items-center gap-6 flex-wrap">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/50">Preview · internal</div>
            <div className="display text-lg mt-0.5">Public seller profile · <span className="font-mono text-base">/s/glow-co</span></div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/55">Viewer:</span>
            <div className="flex items-center gap-1 rounded-full border border-border bg-white p-1">
              {VIEWERS.map(v => (
                <button key={v.key}
                  onClick={() => setViewer(v.key)}
                  className={`rounded-full px-4 h-8 text-[12px] mono uppercase tracking-[0.12em] transition-colors ${viewer === v.key ? "bg-foreground text-background" : "text-foreground/60 hover:text-foreground"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <Link to="/" className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/50 hover:text-foreground ml-2">← Back</Link>
          </div>
        </div>
      </div>

      {/* ── Page body ────────────────────────────────────────────────── */}
      <div className="max-w-[1240px] mx-auto px-8 py-10">
        <Hero viewer={viewer}/>
        <div className="grid lg:grid-cols-[1fr_360px] gap-10 mt-10">
          <div className="space-y-10">
            <About/>
            <Policies/>            {/* ← directly answers your original Q */}
            <Specialties/>
            <FeaturedProducts/>
            <RatingsPlaceholder/>
          </div>
          <aside className="space-y-6">
            <TrustCard/>
            {viewer === "buyer"  && <BuyerRelationshipCard/>}
            {viewer === "anon"   && <AnonCTA/>}
            {viewer === "seller" && <SellerOwnerCard/>}
            <ContactCard viewer={viewer}/>
          </aside>
        </div>
        <FooterNote/>
      </div>
    </div>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────── */
function Hero({ viewer }) {
  return (
    <section className="relative rounded-3xl border border-border bg-white overflow-hidden">
      {/* Cover band */}
      <div className="h-32 bg-[radial-gradient(ellipse_at_top_left,_rgba(207,255,76,0.18),_transparent_60%),linear-gradient(135deg,#0F1419,#1d2530)]"/>
      {viewer === "seller" && (
        <button className="absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur border border-white/25 text-background px-3 h-8 text-[11.5px] mono uppercase tracking-[0.14em] hover:bg-white/20">
          <Edit3 className="w-3.5 h-3.5"/> Edit profile
        </button>
      )}

      <div className="px-8 pb-8 pt-6 flex items-end gap-6 flex-wrap relative">
        <div className="w-24 h-24 rounded-2xl bg-white shadow-sm border border-border grid place-items-center text-3xl display -mt-16 shrink-0">G</div>
        <div className="flex-1 min-w-0 pb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="display text-3xl tracking-tight">Glow Co. Beauty</h1>
            <Badge tone="ok"><ShieldCheck className="w-3 h-3"/> Verified business</Badge>
            <Badge tone="muted"><CheckCircle2 className="w-3 h-3"/> Insurance on file</Badge>
          </div>
          <div className="mt-2 flex items-center gap-x-5 gap-y-1 flex-wrap text-[13px] text-foreground/65">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/> Los Angeles, CA</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/> Founded 2019</span>
            <span className="inline-flex items-center gap-1.5"><Globe className="w-3.5 h-3.5"/> glowco.com</span>
            <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5"/> EIN-verified</span>
          </div>
        </div>
        {viewer === "anon" && (
          <a href="#contact" className="rounded-full bg-foreground text-background px-5 h-11 inline-flex items-center gap-2 text-sm font-medium hover:bg-foreground/90">
            Sign up to order <ArrowUpRight className="w-4 h-4"/>
          </a>
        )}
        {viewer === "buyer" && (
          <a href="#contact" className="rounded-full bg-foreground text-background px-5 h-11 inline-flex items-center gap-2 text-sm font-medium hover:bg-foreground/90">
            <MessageSquare className="w-4 h-4"/> Send message
          </a>
        )}
      </div>
    </section>
  );
}

/* ── ABOUT ────────────────────────────────────────────────────────── */
function About() {
  return (
    <section>
      <SectionTitle eyebrow="About" title="Who we are"/>
      <p className="text-[15px] leading-relaxed text-foreground/85 max-w-2xl">
        Glow Co. is a Los Angeles–based wholesale beauty supplier specializing in clean,
        cruelty-free skincare. We manufacture in California, hold all SKUs in a 22,000 sq-ft
        bonded warehouse, and ship the same day for orders placed before 12pm PT.
      </p>
      <p className="text-[15px] leading-relaxed text-foreground/70 max-w-2xl mt-3">
        Founded by chemists, run by operators. We work with 240+ wholesale partners across
        the US, including Whole Foods, Erewhon, and 80+ independent beauty boutiques.
      </p>
    </section>
  );
}

/* ── POLICIES — the section that answers the original question ───── */
function Policies() {
  const items = [
    { i: Package,        l: "Quality-inspection window", v: "7 days after delivery", h: "Time to file a damage / shortage claim before claims auto-close." },
    { i: Clock,          l: "Default lead time",         v: "3 business days",       h: "From invoice paid (or Net-X terms accepted) to ready-to-ship." },
    { i: RotateCcw,      l: "Returns policy",            v: "30-day return window",  h: "Buyer pays return shipping unless damaged." },
    { i: AlertTriangle,  l: "Damaged orders",            v: "Replacement or credit", h: "Photos required within QI window. Allianz-backed claims for orders > $5k." },
    { i: Truck,          l: "Shipping coverage",         v: "Continental US · CA",   h: "Freight available on orders > 12 cartons. Hazmat surcharge applies." },
    { i: CreditCard,     l: "Payment terms",             v: "Net-30 · Net-60 · Wire", h: "Credit card accepted with 3% surcharge. Net terms require credit approval." },
  ];
  return (
    <section>
      <SectionTitle eyebrow="Trade terms" title="Policies & inspection terms"
        sub="Everything you need to know before placing an order. These are this seller's defaults — your bilateral agreement (MSA) may override individual lines."/>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        {items.map((it, i) => (
          <div key={i} className="rounded-2xl border border-border bg-white p-5 hover:border-foreground/30 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-secondary grid place-items-center">
                <it.i className="w-4 h-4 text-foreground/75"/>
              </div>
              <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">{it.l}</div>
            </div>
            <div className="display text-lg mt-3">{it.v}</div>
            <div className="text-[12.5px] text-foreground/60 mt-1.5 leading-snug">{it.h}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── SPECIALTIES ─────────────────────────────────────────────────── */
function Specialties() {
  const tags = ["Beauty", "Skincare", "Clean / non-toxic", "Cruelty-free", "Made in USA", "Private-label OK", "MOQ from 12 units"];
  return (
    <section>
      <SectionTitle eyebrow="Categories" title="What we sell"/>
      <div className="flex flex-wrap gap-2 mt-5">
        {tags.map(t => (
          <span key={t} className="chip rounded-full border border-border bg-white px-3.5 h-8 inline-flex items-center text-[12.5px] text-foreground/80">{t}</span>
        ))}
      </div>
    </section>
  );
}

/* ── FEATURED PRODUCTS (v2 — shown faded as "coming") ────────────── */
function FeaturedProducts() {
  const skus = [
    { name: "Vitamin-C Serum 30ml",   sku: "GLW-VC-30",   moq: 12,  price: "$8.40" },
    { name: "Hyaluronic Cleanser",    sku: "GLW-HC-150",  moq: 24,  price: "$5.10" },
    { name: "Retinol Night Cream",    sku: "GLW-RN-50",   moq: 12,  price: "$11.20" },
    { name: "Recovery Eye Mask · 5pk",sku: "GLW-EM-5",    moq: 36,  price: "$3.80" },
  ];
  return (
    <section>
      <SectionTitle eyebrow="Catalog" title="Featured products" sub="A taste of what we carry. Sign in to see full pricing tiers and live inventory."/>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        {skus.map(s => (
          <div key={s.sku} className="rounded-2xl border border-border bg-white p-4">
            <div className="aspect-square rounded-xl bg-gradient-to-br from-secondary to-secondary/40 grid place-items-center">
              <Package className="w-7 h-7 text-foreground/30"/>
            </div>
            <div className="mt-3 text-[13px] font-medium leading-tight">{s.name}</div>
            <div className="text-[11px] mono uppercase tracking-[0.12em] text-foreground/50 mt-1">{s.sku}</div>
            <div className="mt-2 flex items-end justify-between">
              <div className="display text-lg">{s.price}</div>
              <div className="text-[11px] text-foreground/55">MOQ {s.moq}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── RATINGS PLACEHOLDER — locked in v1, unlocks in v3 ───────────── */
function RatingsPlaceholder() {
  return (
    <section>
      <SectionTitle eyebrow="Reviews" title="Ratings & reviews"/>
      <div className="mt-5 rounded-2xl border border-dashed border-foreground/20 bg-white/40 p-8 text-center">
        <Lock className="w-5 h-5 text-foreground/40 mx-auto"/>
        <div className="display text-lg mt-3 text-foreground/80">Coming later</div>
        <div className="text-[13px] text-foreground/55 mt-1.5 max-w-sm mx-auto">
          Buyer reviews will turn on once we have a meaningful sample size. We don't want to harm
          early sellers with thin or noisy data.
        </div>
      </div>
    </section>
  );
}

/* ── SIDEBAR · TRUST CARD (always visible) ───────────────────────── */
function TrustCard() {
  const stats = [
    { l: "On platform",       v: "11 mo" },
    { l: "Orders fulfilled",  v: "1,284" },
    { l: "On-time fulfillment", v: "98.4%" },
    { l: "Avg. ship time",    v: "1.2 days" },
  ];
  const checks = [
    "EIN verified · Middesk",
    "Bank account verified · Plaid",
    "Insurance on file · Allianz Trade",
    "Signed AML / KYB attestation",
  ];
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-accent"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Trust signals · live</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        {stats.map(s => (
          <div key={s.l}>
            <div className="display text-xl">{s.v}</div>
            <div className="text-[11px] text-foreground/55 mt-0.5">{s.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-5 border-t border-border space-y-2">
        {checks.map(c => (
          <div key={c} className="flex items-center gap-2 text-[12.5px] text-foreground/75">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0"/>{c}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── SIDEBAR — anonymous CTA ─────────────────────────────────────── */
function AnonCTA() {
  return (
    <div className="rounded-2xl border border-border bg-foreground text-background p-5">
      <div className="display text-xl leading-snug">Ready to order from Glow Co.?</div>
      <div className="text-[12.5px] text-background/65 mt-1.5">Sign up free in 60 seconds. Pre-approved Net-30 on day one if you qualify.</div>
      <Link to="/register" className="mt-4 inline-flex items-center justify-center w-full rounded-full bg-accent text-foreground px-4 h-10 text-sm font-medium hover:bg-accent/90">
        Sign up to order <ArrowUpRight className="w-4 h-4 ml-1"/>
      </Link>
      <Link to="/login" className="mt-2 inline-flex items-center justify-center w-full text-[12.5px] text-background/65 hover:text-background">
        Already a buyer? Sign in →
      </Link>
    </div>
  );
}

/* ── SIDEBAR — buyer relationship card ───────────────────────────── */
function BuyerRelationshipCard() {
  return (
    <div className="rounded-2xl border border-foreground/20 bg-secondary/60 p-5">
      <div className="flex items-center gap-2">
        <Star className="w-3.5 h-3.5 text-foreground/70"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Your history</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <Stat l="Orders" v="14"/>
        <Stat l="Spend YTD" v="$48,200"/>
        <Stat l="On-time" v="100%"/>
        <Stat l="Last order" v="6 days ago"/>
      </div>
      <div className="mt-5 pt-5 border-t border-foreground/10 space-y-2 text-[12.5px]">
        <Row k="Dropship MSA" v="Active"/>
        <Row k="Credit limit" v="$25,000 · Net-30"/>
        <Row k="Auto-approval" v="On"/>
      </div>
    </div>
  );
}

/* ── SIDEBAR — seller's own view ─────────────────────────────────── */
function SellerOwnerCard() {
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/10 p-5">
      <div className="flex items-center gap-2">
        <Edit3 className="w-3.5 h-3.5 text-foreground/70"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">This is your profile</div>
      </div>
      <div className="text-[13px] text-foreground/80 mt-3 leading-relaxed">
        Buyers see this page when they click your name in the catalog or follow your shareable link.
      </div>
      <div className="mt-4 space-y-2">
        <button className="w-full inline-flex items-center justify-between rounded-lg border border-foreground/15 bg-white px-3.5 h-10 text-[13px] hover:bg-foreground/5">
          Edit profile content <ChevronRight className="w-3.5 h-3.5"/>
        </button>
        <button className="w-full inline-flex items-center justify-between rounded-lg border border-foreground/15 bg-white px-3.5 h-10 text-[13px] hover:bg-foreground/5">
          Manage policies <ChevronRight className="w-3.5 h-3.5"/>
        </button>
        <button className="w-full inline-flex items-center justify-between rounded-lg border border-foreground/15 bg-white px-3.5 h-10 text-[13px] hover:bg-foreground/5">
          Copy shareable link <ChevronRight className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
}

/* ── SIDEBAR — contact card ──────────────────────────────────────── */
function ContactCard({ viewer }) {
  return (
    <div id="contact" className="rounded-2xl border border-border bg-white p-5">
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Contact</div>
      <div className="mt-3 space-y-2 text-[13px]">
        <div className="flex items-center gap-2 text-foreground/80"><Mail className="w-3.5 h-3.5"/> wholesale@glowco.com</div>
        <div className="flex items-center gap-2 text-foreground/80"><Globe className="w-3.5 h-3.5"/> glowco.com/wholesale</div>
      </div>
      {viewer === "buyer" && (
        <button className="mt-4 w-full rounded-full bg-foreground text-background px-4 h-10 text-sm font-medium hover:bg-foreground/90">
          Open conversation
        </button>
      )}
      {viewer === "anon" && (
        <div className="mt-4 text-[11.5px] text-foreground/55 leading-relaxed flex items-start gap-2">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
          <span>Direct messaging unlocks once you sign up. Sellers' inboxes are buyer-only to prevent spam.</span>
        </div>
      )}
    </div>
  );
}

/* ── Footer note (designer comments) ─────────────────────────────── */
function FooterNote() {
  return (
    <div className="mt-12 pt-6 border-t border-border text-[11.5px] text-foreground/50 leading-relaxed max-w-3xl">
      <strong>Design notes:</strong> URL is <code>/s/:handle</code> (no-indexed). Same URL, three viewer states.
      Header policies grid directly answers the "how do buyers know seller terms?" question.
      Ratings section is intentionally empty in v1 — placeholder reserves the slot for v3 without rebuilding the page.
      Sidebar swaps between trust + (buyer history | anon CTA | seller owner controls) based on auth.
    </div>
  );
}

/* ── tiny atoms ──────────────────────────────────────────────────── */
function Badge({ children, tone = "muted" }) {
  const cls = tone === "ok"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-secondary text-foreground/75 border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 h-6 text-[11px] mono uppercase tracking-[0.1em] ${cls}`}>
      {children}
    </span>
  );
}
function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">{eyebrow}</div>
      <div className="display text-2xl mt-1.5 tracking-tight">{title}</div>
      {sub && <div className="text-[13px] text-foreground/60 mt-2 max-w-2xl leading-relaxed">{sub}</div>}
    </div>
  );
}
function Stat({ l, v }) {
  return (
    <div>
      <div className="display text-xl">{v}</div>
      <div className="text-[11px] text-foreground/55 mt-0.5">{l}</div>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-foreground/55">{k}</span>
      <span className="text-foreground/85">{v}</span>
    </div>
  );
}
